import { useState } from 'react'
import { track, useEditor } from 'tldraw'
import { useNavigate, useParams } from 'react-router-dom'
import { MousePointer2, Hand, MessageCircle, PencilRuler, Plus, FileScan, MonitorPlay } from 'lucide-react'
import clsx from 'clsx'
import { toast } from 'sonner'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { getCurrentDirection } from '@/i18n/config'
import { useTranslation } from '@/i18n'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
import { apiService } from '@/service/api'
import { useExplainAsVideoEnabled } from '@/features/videoBrief/explainAsVideo'

interface BoardToolbarV2Props {
  isLocked: boolean
  onCommentClick?: () => void
  onToolChange?: () => void
  onAddClick?: () => void
}

const ICON_SIZE = 22

interface ToolItem {
  icon: React.ReactNode
  type: string
  labelKey: string
}

// FS-688: the four canvas tools are ONE category. Draw used to sit alone below
// the divider, which read as its own group for no reason — every one of these
// changes what a click on the canvas does.
const V2_TOOLS: ToolItem[] = [
  { icon: <MousePointer2 size={ICON_SIZE} />, type: 'select', labelKey: 'board:toolbar.tools.select' },
  { icon: <Hand size={ICON_SIZE} />, type: 'hand', labelKey: 'board:toolbar.tools.move' },
  { icon: <MessageCircle size={ICON_SIZE} />, type: 'comment', labelKey: 'board:toolbar.tools.comment' },
  { icon: <PencilRuler size={ICON_SIZE} />, type: 'draw', labelKey: 'board:toolbar.tools.draw' },
]

/** Where the Tag Mapping entry goes — the standing entity-matching review surface. */
const TAG_MAPPING_PATH = '/tag-mapping'

/**
 * The Tag mapping destination, scoped to the board it was opened from (FS-688).
 *
 * The page reads `?board=` on its first render and filters to that board's
 * files, so leaving here means "the tags on the drawings I am looking at"
 * rather than the tenant's whole inventory. Removable there — the scope is a
 * starting point, not a mode.
 */
const tagMappingPath = (boardId?: string) =>
  boardId ? `${TAG_MAPPING_PATH}?board=${encodeURIComponent(boardId)}` : TAG_MAPPING_PATH

const BoardToolbarV2 = track(({
  isLocked,
  onCommentClick,
  onToolChange,
  onAddClick,
}: BoardToolbarV2Props) => {
  void isLocked
  const editor = useEditor()
  const navigate = useNavigate()
  // Both the board and the embed route name this param, so the scope travels
  // from either without the parent having to thread it down as a prop.
  const { board_id: boardId } = useParams()
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  const tooltipSide = isRTL ? 'left' : 'right'
  // "Explain as video" for every file on this board (PRD: Explain as video).
  // Sits with Tag Mapping: a board action, not a canvas tool.
  const explainVideoEnabled = useExplainAsVideoEnabled()
  const [requestingVideo, setRequestingVideo] = useState(false)

  const handleExplainAsVideo = async () => {
    if (!boardId || requestingVideo) return
    setRequestingVideo(true)
    try {
      const receipt = await apiService.createVideoBrief({ workspaceId: boardId })
      toast.success(t('data_catalog:video_brief.requested', { name: receipt.name }))
    } catch {
      toast.error(t('data_catalog:video_brief.error'))
    } finally {
      setRequestingVideo(false)
    }
  }

  const handleToolClick = (toolName: string) => {
    if (!editor) return
    if (toolName === 'comment') {
      onCommentClick?.()
      return
    }
    onToolChange?.()
    editor.setCurrentTool(toolName)
  }

  const currentToolId = editor?.getCurrentToolId()

  const renderToolButton = (tool: ToolItem) => {
    const isActive = currentToolId === tool.type
    return (
      <IntuiTooltip key={tool.type} content={t(tool.labelKey)} side={tooltipSide}>
        <button
          type="button"
          aria-label={t(tool.labelKey)}
          onClick={() => handleToolClick(tool.type)}
          className={clsx(
            'flex items-center justify-center rounded-lg size-10 transition-colors',
            isActive
              ? 'bg-[#0e7490]/12 text-[#0e7490] dark:bg-[#0e7490]/20 dark:text-[#22d3ee]'
              : 'text-muted-foreground hover:bg-[#f0f1f4] dark:text-foreground dark:hover:bg-dark-muted'
          )}
        >
          {tool.icon}
        </button>
      </IntuiTooltip>
    )
  }

  return (
    <div
      className={clsx(
        'absolute top-1/2 -translate-y-1/2 z-20 flex flex-col items-stretch w-[52px]',
        isRTL ? 'right-4' : 'left-4'
      )}
    >
      {/* Single floating column: teal add + tool stack (matches board mockup) */}
      <div className="rounded-xl border border-[#e8eaee] dark:border-dark-border bg-white dark:bg-dark-card shadow-[0_2px_12px_rgba(15,23,42,0.08)] overflow-hidden flex flex-col items-center">
        <IntuiTooltip content={t('board:actions.add_documents', { defaultValue: 'Add' })} side={tooltipSide}>
          <button
            type="button"
            onClick={onAddClick}
            className="flex items-center justify-center w-full aspect-square bg-[#0e7490] hover:bg-[#155e75] transition-colors"
          >
            <Plus className="w-6 h-6 text-white" strokeWidth={2.25} />
          </button>
        </IntuiTooltip>

        <div className="w-full flex flex-col gap-1 items-center p-2 pt-2 border-t border-[#e8eaee] dark:border-dark-border">
          {V2_TOOLS.map(renderToolButton)}

          {/* Second category. Not a canvas tool — it leaves the board for the
              tag-mapping review surface, so it never takes the active state the
              tools above share.

              Hidden in the Fabric embed: that mode allowlists `boardToolbar`
              but deliberately not `boardNavigation`, and nothing rejects the
              navigation once it starts — embed mode is entered by the /embed/*
              path prefix, so this would succeed and take the host iframe to a
              full standalone page rather than fail closed. */}
          {isFeatureAllowedInEmbed('boardNavigation') && (
            <>
              <div className="w-full h-px bg-[#e8eaee] dark:bg-dark-border my-0.5" />
              <IntuiTooltip
                content={t('nav:menu.tag_mapping', { defaultValue: 'Tag Mapping' })}
                side={tooltipSide}
              >
                <button
                  type="button"
                  aria-label={t('nav:menu.tag_mapping', { defaultValue: 'Tag Mapping' })}
                  onClick={() => navigate(tagMappingPath(boardId))}
                  className="flex items-center justify-center rounded-lg size-10 transition-colors text-muted-foreground hover:bg-[#f0f1f4] dark:text-foreground dark:hover:bg-dark-muted"
                >
                  <FileScan size={ICON_SIZE} />
                </button>
              </IntuiTooltip>
              {explainVideoEnabled && boardId && (
                <IntuiTooltip
                  content={t('board:actions.explain_as_video', { defaultValue: 'Explain as video' })}
                  side={tooltipSide}
                >
                  <button
                    type="button"
                    aria-label={t('board:actions.explain_as_video', { defaultValue: 'Explain as video' })}
                    aria-busy={requestingVideo || undefined}
                    disabled={requestingVideo}
                    onClick={() => void handleExplainAsVideo()}
                    className="flex items-center justify-center rounded-lg size-10 transition-colors text-muted-foreground hover:bg-[#f0f1f4] dark:text-foreground dark:hover:bg-dark-muted disabled:opacity-50"
                  >
                    <MonitorPlay size={ICON_SIZE} />
                  </button>
                </IntuiTooltip>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
})

export default BoardToolbarV2
